import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { formatDate } from 'app/helpers/formatDate';
import { DropDownItem } from 'app/interfaces/items/dropDownItem';
import {
    ExposedListLogsExternalSystemTablesRes,
    ExposedListLogsExternalSystemsRes,
} from 'app/interfaces/responses/Exposed/exposedExternalSystemDetailsRes';
import { ExposedListLogsRes } from 'app/interfaces/responses/Exposed/exposedListLogsRes';
import { LogsService } from 'app/services/api/logs/logs.service';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { catchError, map, of as observableOf, startWith, switchMap } from 'rxjs';

@Component({
    selector: 'app-logs',
    templateUrl: './logs.component.html',
    styleUrls: ['./logs.component.scss'],
})
export class LogsComponent implements OnInit {
    //#region Globals

    //Table
    pageSize: number = 50;
    pageNumber: number = 0;
    pageSizes = [5, 10, 25, 50, 100];
    dataSource = new MatTableDataSource<ExposedListLogsRes>();
    logData: ExposedListLogsRes[];
    totalData: number = 0;
    tableColumns: string[] = [];
    @ViewChild('paginator') paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    // Form
    form: UntypedFormGroup;
    systemsDropDown: Array<DropDownItem> = [];
    tablesDropDown: Array<DropDownItem> = [];
    // mat-select treats a null option as "no selection", so "Allt" uses 0 and is sent as null
    readonly allTablesValue = 0;
    isLoading = true;

    // Dropdown
    externalSystems: ExposedListLogsExternalSystemsRes[] = [];
    externalSystemTables: ExposedListLogsExternalSystemTablesRes[] = [];

    //#endregion

    //#region Init
    constructor(
        private formBuilder: UntypedFormBuilder,
        public logsService: LogsService,
        private alertsService: AlertsService,
        public lsrauthService: LsrauthService,
        public translationService: TranslationService
    ) { }

    ngOnInit(): void {
        this.initForm();
        this.initDropdowns();
        this.initTableColumns();

        this.form.get('system').valueChanges.subscribe((val) => {
            this.logsService.getExternalSystemDetails().subscribe((res) => {
                this.externalSystemTables = res.externalSystemTables;
                if (this.externalSystemTables) {
                    this.externalSystemTables.forEach((x) => {
                        if (x.logsExternalSystemId === val) {
                            this.tablesDropDown.push({
                                value: x.logsExternalSystemTableId,
                                viewValue: this.translationService.translate('label_external_system_table_' + x.tableName),
                            });
                        }
                    });
                }
            });
        });
    }

    initForm(): void {
        const dateFrom = new Date();
        dateFrom.setDate(dateFrom.getDate() - 14);

        this.form = this.formBuilder.group({
            system: new UntypedFormControl(null, [Validators.required]),
            type: new UntypedFormControl(this.allTablesValue, [Validators.required]),
            dateFrom: new UntypedFormControl(new Date(dateFrom.toDateString()), [Validators.required]),
            dateTo: new UntypedFormControl(new Date(new Date().toDateString()), [Validators.required]),
            searchString: new UntypedFormControl(''),
        });
    }

    initDropdowns(): void {
        this.logsService.getExternalSystemDetails().subscribe((res) => {
            this.externalSystems = res.externalSystems;
            this.externalSystemTables = res.externalSystemTables;

            if (this.externalSystems) {
                this.externalSystems.forEach((x) => {
                    this.systemsDropDown.push({
                        value: x.logsExternalSystemId,
                        viewValue: x.systemName,
                    });
                });
            }
            this.form.get('system').setValue(this.externalSystems[0].logsExternalSystemId);
        });
    }

    clickFetchData(): void {
        if (this.form.invalid) {
            return;
        }

        this.fetchData();
    }

    fetchData(): any {
        this.pageNumber = 0;
        this.dataSource.paginator = this.paginator;
        this.paginator.pageIndex = 0;

        this.paginator.page
            .pipe(
                startWith({}),
                switchMap(() => {
                    this.isLoading = true;
                    return this.fetchLogs(this.paginator.pageIndex, this.paginator.pageSize, true).pipe(
                        catchError(() => observableOf(null))
                    );
                }),
                map((logData) => {
                    this.isLoading = false;
                    return logData ?? [];
                })
            )
            .subscribe((logData) => {
                this.logData = logData;
                if (logData.length > 0) {
                    this.totalData = logData[0].totalNumberOfEntries;
                    logData.forEach((element) => {
                        if (element.valueBeforeChange != null) {
                            element.valueBeforeChange = Object.entries(JSON.parse(element.valueBeforeChange))
                                .map(([key, value]) => `<span>${key}: ${value}</span><br>`)
                                .join('');
                        }
                        if (element.valueAfterChange != null) {
                            element.valueAfterChange = Object.entries(JSON.parse(element.valueAfterChange))
                                .map(([key, value]) => `<span>${key}: ${value}</span><br>`)
                                .join('');
                        }
                    });
                } else {
                    this.totalData = 0;
                }
                this.dataSource = new MatTableDataSource(logData);
            });
    }

    fetchLogs(pageNumber: number, pageSize: number, scrollToTop = false) {
        const dateFrom = new Date(this.form.get('dateFrom').value);
        dateFrom.setUTCHours(0, 0, 0, 0);
        const dateTo = new Date(this.form.get('dateTo').value);
        dateTo.setUTCHours(23, 59, 59, 999);

        return this.logsService.getExternalSystemLogs({
            searchId: this.form.get('searchString').value,
            systemId: this.form.get('system').value,
            tableId: this.form.get('type').value === this.allTablesValue ? null : this.form.get('type').value,
            dateFrom: dateFrom,
            dateTo: dateTo,
            pageNumber: pageNumber,
            pageSize: pageSize,
        });
    }

    //#endregion

    //#region Table functions
    initTableColumns(): any {
        this.tableColumns = ['id', 'type', 'valueBeforeChange', 'valueAfterChange', 'changeDate', 'changedBy'];
    }

    pageChanged(event: PageEvent): any {
        this.pageNumber = event.pageIndex;
        this.pageSize = event.pageSize;
        this.fetchLogs(this.pageNumber, this.pageSize, true);
    }

    //#endregion

    //#region Helper methods

    formatDate(date) {
        return formatDate(date);
    }

    //#endregion
}
